import {assertAndContinue} from 'Asserts';
import {SchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/TableConstants';
import {Optional} from 'util/Types';

import {LastSyncedUpstreamValue} from '../StructuredTableConstants';

/**
 * A set of properties that together uniquely identify the LastSyncedUpstreamValue for a particular column.
 */
export type LastSyncedUpstreamValueKey = Pick<
  LastSyncedUpstreamValue,
  'columnGroup' | 'columnType' | 'columnProvenanceUuid' | 'fieldId'
>;

/**
 * Finds the last synced upstream value for a given unique LastSyncedUpstreamValueKey using
 * columnProvenanceUuid at first. If the last synced upstream value is not found, we use columnGroup,
 * columnType and fieldId to find the value. (Custom columns opt out of the second check).
 */
export default function getLastSyncedUpstreamValue(
  lastSyncedUpstreamValues: Array<LastSyncedUpstreamValue>,
  key: LastSyncedUpstreamValueKey
): LastSyncedUpstreamValue | null {
  let matchingLastSyncedUpstreamValues = lastSyncedUpstreamValues.filter(
    (x) => x.columnProvenanceUuid != null && x.columnProvenanceUuid === key.columnProvenanceUuid
  );

  if (matchingLastSyncedUpstreamValues.length === 0 && key.columnType !== TableColumnType.CUSTOM) {
    matchingLastSyncedUpstreamValues = lastSyncedUpstreamValues.filter(
      (x) => x.columnGroup === key.columnGroup && x.columnType === key.columnType && x.fieldId === key.fieldId
    );
  }
  assertAndContinue(
    matchingLastSyncedUpstreamValues.length <= 1,
    'Found multiple LastSyncedUpstreamValues for the same unique column key',
    {lastSyncedUpstreamValues}
  );
  return matchingLastSyncedUpstreamValues.length === 0 ? null : matchingLastSyncedUpstreamValues[0];
}

/**
 * A wrappper of the getLastSyncedUpstreamValue which takes in nullable lastSyncedUpstreamValues
 * and columnConstraint.
 */
export function getLastSyncedUpstreamValueWithColumnConstraint(
  lastSyncedUpstreamValues: Optional<Array<LastSyncedUpstreamValue>>,
  columnConstraint: Optional<ColumnConstraint>
): LastSyncedUpstreamValue | null {
  if (!lastSyncedUpstreamValues || !columnConstraint?.columnDefinition) {
    return null;
  }
  return getLastSyncedUpstreamValue(lastSyncedUpstreamValues, {
    columnGroup: columnConstraint.columnDefinition.columnGroup ?? null,
    columnType: columnConstraint.columnDefinition.type,
    columnProvenanceUuid: columnConstraint.structuredTableColumn?.provenanceUuid ?? null,
    fieldId:
      columnConstraint.tagSchemaFieldId?.toString() ??
      (columnConstraint.columnDefinition as SchemaFieldColumnDefinition).tagSchemaFieldApiId ??
      null,
  });
}
